import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Zap, Users, Building2, Euro, TrendingUp, Activity, CreditCard, FileText,
  LogOut, Shield, Sun, Home, Percent, BarChart3, PieChart, UserPlus, Eye, X
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RechartsPie, Pie, Cell
} from 'recharts';
import { AdminUsersTab } from '../components/admin/AdminUsersTab';
import { AdminCommunitiesTab } from '../components/admin/AdminCommunitiesTab';
import { AdminTransactionsTab } from '../components/admin/AdminTransactionsTab';
import { AdminPaymentsTab } from '../components/admin/AdminPaymentsTab';
import { AdminRegistrationsTab } from '../components/admin/AdminRegistrationsTab';
import { AdminSponsorshipsTab } from '../components/admin/AdminSponsorshipsTab';
import { AdminFinancesTab } from '../components/admin/AdminFinancesTab';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [createForm, setCreateForm] = useState({ email: '', password: '', name: '', role: 'client' });
  const [createLoading, setCreateLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [editingCommunity, setEditingCommunity] = useState(null);
  const [communityMembers, setCommunityMembers] = useState([]);
  const [communityEditForm, setCommunityEditForm] = useState({ name: '', price_per_kwh: 0, description: '', city: '', department: '', postal_code: '' });
  const [memberSearch, setMemberSearch] = useState('');
  const [memberSearchResults, setMemberSearchResults] = useState([]);
  const [communityLoading, setCommunityLoading] = useState(false);
  const [pendingRegistrations, setPendingRegistrations] = useState([]);
  const [regLoading, setRegLoading] = useState(false);
  const [sponsorships, setSponsorships] = useState([]);
  const [prosList, setProsList] = useState([]);
  const [unsponsoredUsers, setUnsponsoredUsers] = useState([]);
  const [sponsorLoading, setSponsorLoading] = useState(false);
  const [finances, setFinances] = useState(null);
  const [financesLoading, setFinancesLoading] = useState(false);
  const [producerMargins, setProducerMargins] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    if (isAuthenticated) fetchAllData();
  }, [isAuthenticated]);

  const fetchAllData = async () => {
    try {
      const headers = getAuthHeaders();
      const [dashRes, regRes, sponsorRes, prosRes, finRes, marginsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/dashboard`, { headers }),
        axios.get(`${API_URL}/api/admin/pending-registrations`, { headers }).catch(() => ({ data: { registrations: [] } })),
        axios.get(`${API_URL}/api/admin/sponsorships`, { headers }).catch(() => ({ data: { sponsorships: [] } })),
        axios.get(`${API_URL}/api/admin/pros-list`, { headers }).catch(() => ({ data: { pros: [] } })),
        axios.get(`${API_URL}/api/admin/finances`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/admin/producer-margins`, { headers }).catch(() => ({ data: null }))
      ]);
      setStats(dashRes.data.stats);
      setUsers(dashRes.data.users || []);
      setCommunities(dashRes.data.communities || []);
      setTransactions(dashRes.data.transactions || []);
      setPayments(dashRes.data.payments || []);
      setPendingRegistrations(regRes.data.registrations || []);
      setSponsorships(sponsorRes.data.sponsorships || []);
      setProsList(prosRes.data.pros || []);
      setFinances(finRes.data);
      setProducerMargins(marginsRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Acces non autorise');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => { logout(); navigate('/'); };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!createForm.email || !createForm.password || !createForm.name) {
      toast.error('Tous les champs sont requis'); return;
    }
    setCreateLoading(true);
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API_URL}/api/admin/users/create`, createForm, { headers });
      toast.success(`Utilisateur "${createForm.name}" créé avec le rôle "${createForm.role}"`);
      setShowCreateUser(false);
      setCreateForm({ email: '', password: '', name: '', role: 'client' });
      fetchAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally { setCreateLoading(false); }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API_URL}/api/admin/users/${userId}`, { role: newRole }, { headers });
      toast.success(`Rôle changé en "${newRole}"`);
      fetchAllData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
  };

  const handleToggleActive = async (userId, currentActive) => {
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API_URL}/api/admin/users/${userId}`, { is_active: !currentActive }, { headers });
      toast.success(currentActive ? 'Utilisateur désactivé' : 'Utilisateur réactivé');
      fetchAllData();
    } catch { toast.error('Erreur'); }
  };

  const fetchUserDetail = async (userId) => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API_URL}/api/admin/users/${userId}`, { headers });
      setUserDetail(res.data);
      setSelectedUser(userId);
    } catch { toast.error('Erreur lors du chargement des détails'); }
  };

  const openCommunityEdit = async (community) => {
    setEditingCommunity(community);
    setCommunityEditForm({ 
      name: community.name, price_per_kwh: community.price_per_kwh || 0.15, 
      description: community.description || '', city: community.city || '',
      department: community.department || '', postal_code: community.postal_code || ''
    });
    setCommunityLoading(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API_URL}/api/admin/communities/${community.id}/members`, { headers });
      setCommunityMembers(res.data.members || []);
    } catch { toast.error('Erreur chargement membres'); setCommunityMembers([]); }
    finally { setCommunityLoading(false); }
  };

  const handleSaveCommunity = async () => {
    if (!editingCommunity) return;
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API_URL}/api/admin/communities/${editingCommunity.id}`, communityEditForm, { headers });
      toast.success('Communauté mise à jour');
      fetchAllData();
      setEditingCommunity(prev => ({ ...prev, ...communityEditForm }));
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
  };

  const handleSearchMembers = async (query) => {
    setMemberSearch(query);
    if (query.length < 2) { setMemberSearchResults([]); return; }
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API_URL}/api/admin/users?search=${encodeURIComponent(query)}&limit=10`, { headers });
      const existingIds = communityMembers.map(m => m.user_id);
      setMemberSearchResults((res.data.users || []).filter(u => !existingIds.includes(u.id)));
    } catch { setMemberSearchResults([]); }
  };

  const handleAddMember = async (userId) => {
    if (!editingCommunity) return;
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API_URL}/api/admin/communities/${editingCommunity.id}/members/${userId}`, {}, { headers });
      toast.success('Membre ajouté');
      setMemberSearch(''); setMemberSearchResults([]);
      openCommunityEdit(editingCommunity); fetchAllData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
  };

  const handleRemoveMember = async (userId) => {
    if (!editingCommunity) return;
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API_URL}/api/admin/communities/${editingCommunity.id}/members/${userId}`, { headers });
      toast.success('Membre retiré');
      setCommunityMembers(prev => prev.filter(m => m.user_id !== userId));
      fetchAllData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
  };

  const handleDeleteCommunity = async (communityId, communityName) => {
    if (!window.confirm(`Supprimer définitivement la communauté "${communityName}" et toutes ses données ?`)) return;
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API_URL}/api/admin/communities/${communityId}`, { headers });
      toast.success(`Communauté "${communityName}" supprimée`);
      setEditingCommunity(null); fetchAllData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
  };

  const handleApproveRegistration = async (userId, userName) => {
    setRegLoading(true);
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API_URL}/api/admin/approve-registration/${userId}`, {}, { headers });
      toast.success(`Inscription de "${userName}" validée`);
      fetchAllData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
    finally { setRegLoading(false); }
  };

  const handleRejectRegistration = async (userId, userName) => {
    if (!window.confirm(`Refuser l'inscription de "${userName}" ?`)) return;
    setRegLoading(true);
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API_URL}/api/admin/reject-registration/${userId}`, {}, { headers });
      toast.success(`Inscription de "${userName}" refusée`);
      fetchAllData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
    finally { setRegLoading(false); }
  };

  const handleSendDailyRecap = async () => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${API_URL}/api/admin/send-daily-recap`, {}, { headers });
      toast.success(res.data.message);
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
  };

  const handleAssignSponsor = async (userId, proId) => {
    setSponsorLoading(true);
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API_URL}/api/admin/users/${userId}/assign-sponsor`, { pro_id: proId }, { headers });
      toast.success('Parrain assigne avec succes');
      setUnsponsoredUsers([]);
      fetchAllData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
    finally { setSponsorLoading(false); }
  };

  const handleRemoveSponsor = async (userId) => {
    if (!window.confirm('Retirer le parrain de cet utilisateur ?')) return;
    setSponsorLoading(true);
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API_URL}/api/admin/users/${userId}/remove-sponsor`, { headers });
      toast.success('Parrain retire');
      fetchAllData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
    finally { setSponsorLoading(false); }
  };

  const handleSearchUnsponsored = async (query) => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API_URL}/api/admin/unsponsored-users?search=${encodeURIComponent(query)}`, { headers });
      setUnsponsoredUsers(res.data.users || []);
    } catch { setUnsponsoredUsers([]); }
  };

  const handleUpdateMargin = async (communityId, buyPrice, sellPrice, commission) => {
    try {
      const headers = getAuthHeaders();
      const payload = { price_per_kwh: buyPrice, sell_price_per_kwh: sellPrice };
      if (commission !== undefined && commission !== null) {
        payload.commission_per_kwh = commission;
      }
      await axios.put(`${API_URL}/api/admin/communities/${communityId}/margins`, payload, { headers });
      toast.success('Marges et commission mises a jour. Emails envoyes aux membres concernes.');
      fetchAllData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur lors de la mise a jour'); }
  };

  const getRoleBadge = (role) => {
    const map = {
      admin: { label: 'Admin', cls: 'bg-purple-100 text-purple-700' },
      pro: { label: 'Pro', cls: 'bg-amber-100 text-amber-700' },
      client: { label: 'Client', cls: 'bg-blue-100 text-blue-700' },
    };
    const b = map[role] || map.client;
    return <Badge className={b.cls}>{b.label}</Badge>;
  };

  if (!isAuthenticated) { navigate('/login'); return null; }
  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><div className="loader" /></div>;

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const userTypeData = [
    { name: 'Producteurs', value: stats?.producers_count || 0 },
    { name: 'Consommateurs', value: stats?.consumers_count || 0 }
  ];
  const revenueData = stats?.daily_revenue || [];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/95 backdrop-blur-md text-white border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Green Teva" className="w-9 h-9 rounded-xl object-cover" />
              <div>
                <span className="text-base font-bold leading-tight block">Administration</span>
                <p className="text-[10px] text-slate-400 leading-tight">Green Teva Énergie</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-slate-800 text-xs h-8">
                  <Home className="w-3.5 h-3.5 mr-1.5" /> Retour
                </Button>
              </Link>
              <Button variant="ghost" size="icon" onClick={handleLogout} className="text-slate-400 hover:text-white hover:bg-slate-800 h-8 w-8">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-20 pb-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="bg-white border border-green-100/60 rounded-2xl overflow-hidden">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-9 h-9 rounded-lg bg-green-50 border border-green-100 flex items-center justify-center"><Euro className="w-4 h-4 text-green-600" /></div>
                  <TrendingUp className="w-4 h-4 text-green-500" />
                </div>
                <p className="text-2xl font-bold text-foreground mono">{stats?.total_revenue?.toFixed(0) || '0'}€</p>
                <p className="text-xs text-muted-foreground mt-1">Revenus total</p>
              </CardContent>
            </Card>
            <Card className="bg-white border border-blue-100/60 rounded-2xl overflow-hidden">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-9 h-9 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center"><Users className="w-4 h-4 text-blue-600" /></div>
                  <span className="text-[10px] font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full">{stats?.new_users_week || 0} /sem</span>
                </div>
                <p className="text-2xl font-bold text-foreground mono">{stats?.total_users || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">Utilisateurs</p>
              </CardContent>
            </Card>
            <Card className="bg-white border border-amber-100/60 rounded-2xl overflow-hidden">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-9 h-9 rounded-lg bg-amber-50 border border-amber-100 flex items-center justify-center"><Building2 className="w-4 h-4 text-amber-600" /></div>
                </div>
                <p className="text-2xl font-bold text-foreground mono">{stats?.total_communities || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">Communautés actives</p>
              </CardContent>
            </Card>
            <Card className="bg-white border border-purple-100/60 rounded-2xl overflow-hidden">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-9 h-9 rounded-lg bg-purple-50 border border-purple-100 flex items-center justify-center"><Zap className="w-4 h-4 text-purple-600" /></div>
                </div>
                <p className="text-2xl font-bold text-foreground mono">{stats?.total_energy_kwh?.toFixed(0) || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">kWh échangés</p>
              </CardContent>
            </Card>
          </div>

          {/* Secondary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="border border-slate-100"><CardContent className="p-4 flex items-center gap-3"><div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center shrink-0"><CreditCard className="w-4 h-4 text-green-600" /></div><div className="min-w-0"><p className="text-[11px] text-muted-foreground truncate">Droits d'entrée</p><p className="text-lg font-bold mono">{stats?.entry_fees_collected?.toFixed(0) || '0'}€</p></div></CardContent></Card>
            <Card className="border border-slate-100"><CardContent className="p-4 flex items-center gap-3"><div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0"><Percent className="w-4 h-4 text-blue-600" /></div><div className="min-w-0"><p className="text-[11px] text-muted-foreground truncate">Commissions</p><p className="text-lg font-bold mono">{stats?.commissions_collected?.toFixed(0) || '0'}€</p></div></CardContent></Card>
            <Card className="border border-slate-100"><CardContent className="p-4 flex items-center gap-3"><div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center shrink-0"><FileText className="w-4 h-4 text-amber-600" /></div><div className="min-w-0"><p className="text-[11px] text-muted-foreground truncate">Transactions</p><p className="text-lg font-bold mono">{stats?.total_transactions || 0}</p></div></CardContent></Card>
            <Card className="border border-slate-100"><CardContent className="p-4 flex items-center gap-3"><div className="w-9 h-9 rounded-lg bg-purple-50 flex items-center justify-center shrink-0"><Activity className="w-4 h-4 text-purple-600" /></div><div className="min-w-0"><p className="text-[11px] text-muted-foreground truncate">Mandats SEPA</p><p className="text-lg font-bold mono">{stats?.sepa_mandates || 0}</p></div></CardContent></Card>
          </div>

          {/* Charts Row */}
          <div className="grid lg:grid-cols-3 gap-6 mb-8">
            <Card className="lg:col-span-2">
              <CardHeader><CardTitle className="flex items-center gap-2"><BarChart3 className="w-5 h-5" /> Revenus des 30 derniers jours</CardTitle></CardHeader>
              <CardContent>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={revenueData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                      <YAxis unit="€" tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(value) => [`${value}€`]} />
                      <Area type="monotone" dataKey="revenue" stroke="#22c55e" fill="#22c55e" fillOpacity={0.3} name="Revenus" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="flex items-center gap-2"><PieChart className="w-5 h-5" /> Répartition utilisateurs</CardTitle></CardHeader>
              <CardContent>
                <div className="h-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPie>
                      <Pie data={userTypeData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={5} dataKey="value">
                        {userTypeData.map((_, index) => (<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />))}
                      </Pie>
                      <Tooltip />
                    </RechartsPie>
                  </ResponsiveContainer>
                </div>
                <div className="flex justify-center gap-6 mt-4">
                  <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-green-500" /><span className="text-sm">Producteurs ({stats?.producers_count || 0})</span></div>
                  <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-blue-500" /><span className="text-sm">Consommateurs ({stats?.consumers_count || 0})</span></div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="registrations" className="space-y-6">
            <TabsList>
              <TabsTrigger value="registrations" className="relative" data-testid="tab-registrations">
                Inscriptions
                {pendingRegistrations.length > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 bg-amber-500 text-white text-[10px] rounded-full min-w-[18px] text-center font-bold">
                    {pendingRegistrations.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="users">Utilisateurs</TabsTrigger>
              <TabsTrigger value="sponsorships" data-testid="tab-sponsorships">Parrainages</TabsTrigger>
              <TabsTrigger value="finances" data-testid="tab-finances">Finances</TabsTrigger>
              <TabsTrigger value="communities">Communautes</TabsTrigger>
              <TabsTrigger value="transactions">Transactions</TabsTrigger>
              <TabsTrigger value="payments">Paiements</TabsTrigger>
            </TabsList>

            <TabsContent value="registrations">
              <AdminRegistrationsTab
                pendingRegistrations={pendingRegistrations}
                handleApprove={handleApproveRegistration}
                handleReject={handleRejectRegistration}
                handleSendRecap={handleSendDailyRecap}
                loading={regLoading}
              />
            </TabsContent>

            <TabsContent value="users">
              <AdminUsersTab
                users={users} filteredUsers={filteredUsers} searchTerm={searchTerm}
                setSearchTerm={setSearchTerm} setShowCreateUser={setShowCreateUser}
                getRoleBadge={getRoleBadge} fetchUserDetail={fetchUserDetail}
                handleChangeRole={handleChangeRole} handleToggleActive={handleToggleActive}
                userDetail={userDetail} setUserDetail={setUserDetail} setSelectedUser={setSelectedUser}
              />
            </TabsContent>

            <TabsContent value="sponsorships">
              <AdminSponsorshipsTab
                sponsorships={sponsorships}
                prosList={prosList}
                unsponsoredUsers={unsponsoredUsers}
                onAssignSponsor={handleAssignSponsor}
                onRemoveSponsor={handleRemoveSponsor}
                onSearchUnsponsored={handleSearchUnsponsored}
                loading={sponsorLoading}
              />
            </TabsContent>

            <TabsContent value="finances">
              <AdminFinancesTab
                finances={finances}
                loading={financesLoading}
                producerMargins={producerMargins}
                onUpdateMargin={handleUpdateMargin}
                onRefresh={fetchAllData}
              />
            </TabsContent>

            <TabsContent value="communities">
              <AdminCommunitiesTab
                communities={communities} openCommunityEdit={openCommunityEdit}
                editingCommunity={editingCommunity} setEditingCommunity={setEditingCommunity}
                communityEditForm={communityEditForm} setCommunityEditForm={setCommunityEditForm}
                handleSaveCommunity={handleSaveCommunity} handleDeleteCommunity={handleDeleteCommunity}
                communityMembers={communityMembers} communityLoading={communityLoading}
                memberSearch={memberSearch} handleSearchMembers={handleSearchMembers}
                memberSearchResults={memberSearchResults} handleAddMember={handleAddMember}
                handleRemoveMember={handleRemoveMember} setMemberSearch={setMemberSearch}
                setMemberSearchResults={setMemberSearchResults}
              />
            </TabsContent>

            <TabsContent value="transactions">
              <AdminTransactionsTab transactions={transactions} />
            </TabsContent>

            <TabsContent value="payments">
              <AdminPaymentsTab payments={payments} />
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* Create User Modal */}
      {showCreateUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="create-user-modal">
          <Card className="w-full max-w-md bg-white rounded-2xl shadow-2xl">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold">Créer un utilisateur</CardTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowCreateUser(false)} data-testid="close-create-modal">
                <X className="w-5 h-5" />
              </Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Nom *</Label>
                  <Input value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} placeholder="Jean Dupont" className="rounded-xl" required data-testid="create-user-name" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Email *</Label>
                  <Input type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} placeholder="utilisateur@email.fr" className="rounded-xl" required data-testid="create-user-email" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Mot de passe *</Label>
                  <Input type="password" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} placeholder="Min. 6 caractères" className="rounded-xl" required minLength={6} data-testid="create-user-password" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Rôle</Label>
                  <div className="grid grid-cols-3 gap-2" data-testid="create-user-role-select">
                    {[
                      { value: 'client', label: 'Client', icon: Home, cls: 'border-blue-200 bg-blue-50 text-blue-700' },
                      { value: 'pro', label: 'Pro', icon: Sun, cls: 'border-amber-200 bg-amber-50 text-amber-700' },
                      { value: 'admin', label: 'Admin', icon: Shield, cls: 'border-purple-200 bg-purple-50 text-purple-700' },
                    ].map(({ value, label, icon: Icon, cls }) => (
                      <button
                        key={value} type="button"
                        onClick={() => setCreateForm({ ...createForm, role: value })}
                        className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all ${
                          createForm.role === value ? cls + ' ring-2 ring-offset-1' : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50'
                        }`}
                        data-testid={`create-role-${value}`}
                      >
                        <Icon className="w-5 h-5" /><span className="text-xs font-semibold">{label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <Button type="submit" disabled={createLoading} className="w-full bg-green-600 hover:bg-green-700 text-white rounded-full h-12 font-semibold" data-testid="submit-create-user">
                  {createLoading ? <div className="loader w-5 h-5 border-2 border-white/30 border-t-white" /> : <><UserPlus className="w-4 h-4 mr-2" /> Créer l'utilisateur</>}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
